import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Send, ArrowRight } from "lucide-react";
import { PROFILE } from "@/lib/portfolioData";
import SectionHeader from "@/components/SectionHeader";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — thank you! I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : (detail || "Failed to send. Try again.");
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-36 px-6 md:px-10 bg-[#0d0d0d]"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          number="08"
          tag="Get in Touch"
          title={<>
            Let's Create
            <br />
            <span className="text-gradient-gold">Something Amazing</span>
          </>}
          kicker="Brands, tourism boards, and creators — I'd love to hear about your project."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Info */}
          <Reveal className="lg:col-span-5">
            <div className="space-y-4">
              <a
                href={`mailto:${PROFILE.email}`}
                className="flex items-start gap-4 p-6 rounded-2xl bg-[#161616] border border-white/5 raise-hover group"
                data-testid="contact-email"
              >
                <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border border-[#D4AF37]/25 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37] transition-colors">
                  <Mail className="w-4 h-4 text-[#D4AF37] group-hover:text-black transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-1">Email</p>
                  <p className="font-display text-white text-lg break-all">{PROFILE.email}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-all self-center" />
              </a>

              <div className="flex items-start gap-4 p-6 rounded-2xl bg-[#161616] border border-white/5">
                <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-1">Phone</p>
                  {PROFILE.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="block font-display text-white text-base hover:text-[#D4AF37] transition-colors"
                      data-testid={`contact-phone-${p.replace(/\s|\+/g, "")}`}
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-2xl bg-[#161616] border border-white/5">
                <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-1">Location</p>
                  <p className="font-display text-white text-lg">{PROFILE.location}</p>
                </div>
              </div>

              <a
                href={PROFILE.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 p-6 rounded-2xl bg-[#161616] border border-white/5 raise-hover group"
                data-testid="contact-instagram"
              >
                <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border border-[#D4AF37]/25 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37] transition-colors">
                  <Instagram className="w-4 h-4 text-[#D4AF37] group-hover:text-black transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-1">Instagram</p>
                  <p className="font-display text-white text-lg">@{PROFILE.instagram}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-all self-center" />
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal className="lg:col-span-7" delay={150}>
            <form
              onSubmit={onSubmit}
              className="glass rounded-2xl p-8 md:p-10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
                    Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    className="tk-input"
                    required
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
                    Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    className="tk-input"
                    required
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
                  Subject
                </label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="Project, collaboration, or inquiry"
                  className="tk-input"
                  required
                  data-testid="contact-input-subject"
                />
              </div>

              <div className="mt-5">
                <label className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2 block">
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  placeholder="Tell me about your vision, timeline, and goals..."
                  className="tk-input resize-none"
                  required
                  data-testid="contact-input-message"
                />
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-white/50 max-w-md">
                  I typically respond within 24 hours. For urgent projects, drop a note on Instagram.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="magnetic-btn solid disabled:opacity-70"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <span className="tk-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Let's Create Something Amazing
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
